import { useCheckout } from '@/hooks/useCheckout'
import { usePortal } from '@/hooks/usePortal'
import { useSubscription } from '@/hooks/useSubscription'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/authStore'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

const PRO_MONTHLY_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_PRO_MONTHLY ?? ''
const PRO_ANNUAL_PRICE_ID = import.meta.env.VITE_STRIPE_PRICE_PRO_ANNUAL ?? ''

function nextResetDate(windowStartsAt: string): string {
  const reset = new Date(new Date(windowStartsAt).getTime() + 30 * 24 * 60 * 60 * 1000)
  return reset.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })
}

export default function SettingsPage() {
  const navigate = useNavigate()
  const user = useAuthStore((s) => s.user)
  const signOut = useAuthStore((s) => s.signOut)
  const {
    profile,
    isPro,
    isAdmin,
    isGiftedProActive,
    sessionsRemaining,
    loading: subLoading,
  } = useSubscription()
  const { loading: checkoutLoading, error: checkoutError, startCheckout } = useCheckout()
  const { loading: portalLoading, error: portalError, openPortal } = usePortal()

  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'annual'>('monthly')
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [deleteInput, setDeleteInput] = useState('')
  const [deleting, setDeleting] = useState(false)
  const [deleteError, setDeleteError] = useState<string | null>(null)

  const [editingName, setEditingName] = useState(false)
  const [nameInput, setNameInput] = useState('')
  const [nameSaving, setNameSaving] = useState(false)
  const [nameError, setNameError] = useState<string | null>(null)
  const [savedName, setSavedName] = useState<string | null>(null)

  const displayName = savedName ?? profile?.name ?? user?.email?.split('@')[0] ?? 'Account'

  function openNameEdit() {
    setNameInput(savedName ?? profile?.name ?? '')
    setNameError(null)
    setEditingName(true)
  }

  async function handleSaveName() {
    if (!user) return
    const trimmed = nameInput.trim()
    if (!trimmed) {
      setNameError('Name cannot be empty.')
      return
    }
    if (!user) return
    setNameSaving(true)
    setNameError(null)
    const { error } = await supabase.from('profiles').update({ name: trimmed }).eq('id', user.id)
    if (error) {
      setNameError('Failed to save. Please try again.')
      setNameSaving(false)
      return
    }
    setSavedName(trimmed)
    setEditingName(false)
    setNameSaving(false)
  }

  async function handleUpgrade() {
    const priceId = selectedPlan === 'annual' ? PRO_ANNUAL_PRICE_ID : PRO_MONTHLY_PRICE_ID
    if (!priceId) return
    await startCheckout(priceId)
  }

  async function handleDeleteAccount() {
    if (deleteInput !== 'delete my account') return
    setDeleting(true)
    setDeleteError(null)

    // Cascade-deletes all PII: sessions, answers, scores, reports (CCPA requirement)
    const { error } = await supabase.functions.invoke('delete-account', {})
    if (error) {
      setDeleteError('Failed to delete account. Please try again or contact support.')
      setDeleting(false)
      return
    }

    await signOut()
    navigate('/', { replace: true })
  }

  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border bg-background/80 backdrop-blur-sm">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-3">
          <Link
            to="/dashboard"
            className="text-sm text-muted-foreground hover:text-foreground transition-colors"
          >
            ← Dashboard
          </Link>
          <span className="text-muted-foreground">/</span>
          <span className="text-sm font-medium">Settings</span>
        </div>
      </header>

      <div className="mx-auto max-w-2xl space-y-8 px-4 py-8">
        {/* Account info */}
        <section className="rounded-xl border border-border p-6 space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            Account
          </h2>

          {/* Name */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Name</p>
            {editingName ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={nameInput}
                  onChange={(e) => setNameInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSaveName()
                    if (e.key === 'Escape') setEditingName(false)
                  }}
                  placeholder="Your name"
                  className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                />
                {nameError && (
                  <p className="text-xs text-destructive" role="alert">
                    {nameError}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => void handleSaveName()}
                    disabled={nameSaving}
                    className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:opacity-50"
                  >
                    {nameSaving ? 'Saving…' : 'Save'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditingName(false)}
                    className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <p className="font-medium">{displayName}</p>
                <button
                  type="button"
                  onClick={openNameEdit}
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  Edit
                </button>
              </div>
            )}
          </div>

          {/* Email */}
          <div className="space-y-1.5">
            <p className="text-xs font-medium text-muted-foreground">Email</p>
            <p className="text-sm">{user?.email}</p>
          </div>
        </section>

        {/* Subscription */}
        <section className="rounded-xl border border-border p-6 space-y-5">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              Subscription
            </h2>
            {!subLoading && (
              <span
                className={cn(
                  'rounded-full px-2.5 py-0.5 text-xs font-semibold',
                  isPro ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'
                )}
              >
                {isPro ? 'Pro' : 'Free'}
              </span>
            )}
          </div>

          {subLoading ? (
            <div className="h-16 animate-pulse rounded-lg bg-muted" />
          ) : isPro ? (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {isAdmin
                  ? 'Admin access — unlimited sessions.'
                  : isGiftedProActive
                    ? `Pro access granted${profile?.giftedProExpiresAt ? ` until ${new Date(profile.giftedProExpiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}` : ''}.`
                    : "You're on the Pro plan — unlimited practice sessions every month."}
              </p>
              {!isAdmin && !isGiftedProActive && (
                <>
                  {portalError && (
                    <p className="text-sm text-destructive" role="alert">
                      {portalError}
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={() => void openPortal()}
                    disabled={portalLoading}
                    className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {portalLoading ? 'Opening…' : 'Manage subscription'}
                  </button>
                </>
              )}
            </div>
          ) : (
            <div className="space-y-5">
              <p className="text-sm text-muted-foreground">
                Free plan &middot;{' '}
                {sessionsRemaining === Number.POSITIVE_INFINITY ? 'unlimited' : sessionsRemaining}{' '}
                session
                {sessionsRemaining === 1 ? '' : 's'} remaining this month
              </p>
              {profile?.sessionWindowStartsAt && (
                <p className="text-xs text-muted-foreground">
                  Credits reset {nextResetDate(profile.sessionWindowStartsAt)}
                </p>
              )}

              {/* Plan toggle */}
              <div className="flex rounded-lg border border-border p-1 w-fit">
                {(['monthly', 'annual'] as const).map((plan) => (
                  <button
                    key={plan}
                    type="button"
                    onClick={() => setSelectedPlan(plan)}
                    className={cn(
                      'rounded-md px-4 py-1.5 text-sm font-medium transition-colors',
                      selectedPlan === plan
                        ? 'bg-foreground text-background'
                        : 'text-muted-foreground hover:text-foreground'
                    )}
                  >
                    {plan === 'monthly' ? 'Monthly · $8' : 'Annual · $59'}
                    {plan === 'annual' && (
                      <span className="ml-1.5 rounded-full bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                        SAVE 38%
                      </span>
                    )}
                  </button>
                ))}
              </div>

              {/* What you have */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  What you have now
                </p>
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {[
                    '3 sessions every 30 days',
                    'STAR scoring on every answer',
                    'Adaptive follow-up questions',
                    'Post-interview feedback report',
                    'Session history (last 3)',
                    'Per-session competency scores',
                    'Drill Mode practice',
                    'Job description upload',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2">
                      <span aria-hidden="true">✓</span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {/* What Pro adds */}
              <div className="space-y-1.5">
                <p className="text-xs font-semibold uppercase tracking-wide text-primary">
                  Pro unlocks
                </p>
                <ul className="space-y-1 text-sm">
                  {[
                    'Unlimited sessions',
                    'Full answer review after every session',
                    'Full session history',
                    'Competency trend dashboard',
                    'Answer Rewrite Coach',
                    'Priority access to new features',
                  ].map((f) => (
                    <li key={f} className="flex items-center gap-2 font-medium">
                      <span className="text-primary" aria-hidden="true">
                        ✓
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>

              {checkoutError && (
                <p className="text-sm text-destructive" role="alert">
                  {checkoutError}
                </p>
              )}

              <button
                type="button"
                onClick={handleUpgrade}
                disabled={checkoutLoading || !PRO_MONTHLY_PRICE_ID}
                className="w-full rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {checkoutLoading
                  ? 'Redirecting to checkout…'
                  : `Upgrade to Pro · ${selectedPlan === 'annual' ? '$59/yr' : '$8/mo'}`}
              </button>

              {!PRO_MONTHLY_PRICE_ID && (
                <p className="text-xs text-muted-foreground text-center">
                  Checkout not configured. Add{' '}
                  <code className="font-mono">VITE_STRIPE_PRICE_PRO_MONTHLY</code> to your
                  environment.
                </p>
              )}
            </div>
          )}
        </section>

        {/* Danger zone */}
        <section className="rounded-xl border border-destructive/30 p-6 space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-destructive">
            Danger zone
          </h2>
          <p className="text-sm text-muted-foreground">
            Deleting your account permanently removes all your sessions, answers, scores, and
            reports. This action cannot be undone.
          </p>

          {!deleteConfirmOpen ? (
            <button
              type="button"
              onClick={() => setDeleteConfirmOpen(true)}
              className="rounded-lg border border-destructive/40 px-4 py-2 text-sm font-medium text-destructive hover:bg-destructive/5 transition-colors"
            >
              Delete my account
            </button>
          ) : (
            <div className="space-y-3">
              <p className="text-sm font-medium">
                Type <span className="font-mono font-semibold">delete my account</span> to confirm:
              </p>
              <input
                type="text"
                value={deleteInput}
                onChange={(e) => setDeleteInput(e.target.value)}
                placeholder="delete my account"
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-destructive/40"
              />
              {deleteError && (
                <p className="text-sm text-destructive" role="alert">
                  {deleteError}
                </p>
              )}
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={handleDeleteAccount}
                  disabled={deleting || deleteInput !== 'delete my account'}
                  className="rounded-lg bg-destructive px-4 py-2 text-sm font-semibold text-destructive-foreground transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {deleting ? 'Deleting…' : 'Confirm delete'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDeleteConfirmOpen(false)
                    setDeleteInput('')
                    setDeleteError(null)
                  }}
                  className="rounded-lg border border-border px-4 py-2 text-sm font-medium hover:bg-muted transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </main>
  )
}
